import React, { useEffect, useState } from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';

import {
  ActiveDefectLayer,
  ActiveDefectLayerId,
} from 'state/surveyDataManager';

import ActiveLayerBasicSettings from './ActiveLayerBasicSettings';
import ActiveLayerAdvancedSettings from './ActiveLayerAdvancedSettings';
import { DefectCatalogItem } from 'domain/surveyDataManager/contracts';
import { canShowStaffVisualisationSettings } from 'domain/surveyDataManager/entitlements/stubEntitlements';
import type { ActiveLayerVisualizationSettings } from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';
import {
  FAVOURITES_CHANGED_EVENT,
  type FavouriteLayerNode,
  type FavouritesChangedDetail,
  dispatchFavouritesChanged,
  getFavouriteBaseKeys,
  loadFavouritesMap,
  saveFavouritesMap,
  toggleFavourite,
} from 'utils/favouritesStorage';
import './ActiveLayersPanel.scss';

const SDM_FAVOURITES_PARENT_NAME = 'Survey Data Viewer';

function buildSdmFavouriteNode(layer: ActiveDefectLayer): FavouriteLayerNode {
  return {
    key: layer.id,
    title: layer.displayName,
    endPoint: layer.vectorSourceUrl ?? undefined,
    endpoint: layer.vectorSourceUrl ?? undefined,
    type: layer.layerKind,
    category: SDM_FAVOURITES_PARENT_NAME,
    isLeaf: true,
  };
}

export interface ActiveLayersPanelProps {
  readonly layers: readonly ActiveDefectLayer[];
  readonly defects: readonly DefectCatalogItem[];
  readonly expandedLayerId: ActiveDefectLayerId | null;
  readonly advancedSettingsLayerId: ActiveDefectLayerId | null;
  readonly embedded?: boolean;
  readonly isAddLayerOpen?: boolean;
  readonly isAddLayerLoading?: boolean;
  readonly isAddingLayer?: boolean;
  onAddLayer: () => void;
  onToggleVisibility: (layerId: ActiveDefectLayerId) => void;
  onRemove: (layerId: ActiveDefectLayerId) => void;
  onToggleSettings: (layerId: ActiveDefectLayerId) => void;
  onToggleAdvancedSettings: (layerId: ActiveDefectLayerId) => void;
  onDisplayNameChange: (layerId: ActiveDefectLayerId, displayName: string) => void;
  onOpacityChange: (layerId: ActiveDefectLayerId, opacity: number) => void;
  onHighlightToggle: (layerId: ActiveDefectLayerId) => void;
  onLineWidthChange: (layerId: ActiveDefectLayerId, lineWidth: number) => void;
  onLineStyleChange: (layerId: ActiveDefectLayerId, lineStyle: string) => void;
  onVisualizationSettingsSave: (
    layerId: ActiveDefectLayerId,
    settings: ActiveLayerVisualizationSettings,
  ) => void;
}

function ActiveLayersPanel({
  layers,
  defects,
  expandedLayerId,
  advancedSettingsLayerId,
  embedded = false,
  isAddLayerOpen = false,
  isAddLayerLoading = false,
  isAddingLayer = false,
  onAddLayer,
  onToggleVisibility,
  onRemove,
  onToggleSettings,
  onToggleAdvancedSettings,
  onDisplayNameChange,
  onOpacityChange,
  onHighlightToggle,
  onLineWidthChange,
  onLineStyleChange,
  onVisualizationSettingsSave,
}: ActiveLayersPanelProps): JSX.Element {
  const hasLayers = layers.length > 0;
  const showAdvancedSettings = canShowStaffVisualisationSettings();
  const [favouriteLayerKeys, setFavouriteLayerKeys] = useState<Set<string>>(
    () => getFavouriteBaseKeys(loadFavouritesMap()),
  );

  useEffect(() => {
    const onFavouritesChanged = (event: Event) => {
      const detail = (event as CustomEvent<FavouritesChangedDetail>).detail;
      if (!detail) {
        return;
      }
      setFavouriteLayerKeys(getFavouriteBaseKeys(new Map(detail.entries)));
    };
    window.addEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
    return () =>
      window.removeEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
  }, []);

  const handleFavouriteClick = (
    event: React.MouseEvent,
    layer: ActiveDefectLayer,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const current = loadFavouritesMap();
    const { favourites: nextFavourites, isAdding } = toggleFavourite(
      current,
      buildSdmFavouriteNode(layer),
      SDM_FAVOURITES_PARENT_NAME,
    );
    saveFavouritesMap(nextFavourites);
    setFavouriteLayerKeys(getFavouriteBaseKeys(nextFavourites));
    dispatchFavouritesChanged(nextFavourites, isAdding);
  };

  const listOrEmpty = hasLayers ? (
    <ul className="sdm-active-layers__list">
      {layers.map((layer) => {
        const isExpanded = expandedLayerId === layer.id;
        const isFavourite = favouriteLayerKeys.has(layer.id);

        return (
          <li
            key={layer.id}
            className={`sdm-active-layers__item${isExpanded ? ' sdm-active-layers__item--expanded' : ''}${layer.highlighted ? ' sdm-active-layers__item--highlighted' : ''}`}
          >
            <div className="sdm-active-layers__item-row">
              <div className="sdm-active-layers__item-info">
                <span className="sdm-active-layers__item-name">
                  {layer.displayName}
                </span>
                <span className="sdm-active-layers__item-meta">
                  {layer.measurementSlug} / {layer.surveyYearSlug}
                </span>
              </div>
              <div className="sdm-active-layers__item-actions">
                <button
                  type="button"
                  className="sdm-active-layers__favourite-button"
                  title={
                    isFavourite ? 'Remove from favourites' : 'Add to favourites'
                  }
                  aria-label={
                    isFavourite
                      ? `Remove ${layer.displayName} from favourites`
                      : `Add ${layer.displayName} to favourites`
                  }
                  aria-pressed={isFavourite}
                  onClick={(event) => handleFavouriteClick(event, layer)}
                >
                  {isFavourite ? (
                    <AiFillStar color="#FFC845" size={18} aria-hidden />
                  ) : (
                    <AiOutlineStar size={18} aria-hidden />
                  )}
                </button>
                <button
                  type="button"
                  className="sdm-active-layers__icon-button"
                  onClick={() => onToggleSettings(layer.id)}
                  aria-expanded={isExpanded}
                  aria-label={
                    isExpanded
                      ? `Hide settings for ${layer.displayName}`
                      : `Show settings for ${layer.displayName}`
                  }
                >
                  {isExpanded ? 'Hide settings' : 'Settings'}
                </button>
                <button
                  type="button"
                  className="sdm-active-layers__icon-button"
                  onClick={() => onToggleVisibility(layer.id)}
                  aria-pressed={layer.visible}
                  aria-label={
                    layer.visible
                      ? `Hide ${layer.displayName}`
                      : `Show ${layer.displayName}`
                  }
                >
                  {layer.visible ? 'Hide' : 'Show'}
                </button>
                <button
                  type="button"
                  className="sdm-active-layers__icon-button sdm-active-layers__icon-button--danger"
                  onClick={() => onRemove(layer.id)}
                  aria-label={`Remove ${layer.displayName}`}
                >
                  Remove
                </button>
              </div>
            </div>

            {isExpanded ? (
              <>
                <ActiveLayerBasicSettings
                  layer={layer}
                  defects={defects}
                  onDisplayNameChange={(displayName) =>
                    onDisplayNameChange(layer.id, displayName)
                  }
                  onOpacityChange={(opacity) => onOpacityChange(layer.id, opacity)}
                  onLineWidthChange={(lineWidth) =>
                    onLineWidthChange(layer.id, lineWidth)
                  }
                  onLineStyleChange={(lineStyle) =>
                    onLineStyleChange(layer.id, lineStyle)
                  }
                  onHighlightToggle={() => onHighlightToggle(layer.id)}
                />
                {showAdvancedSettings ? (
                  <ActiveLayerAdvancedSettings
                    layer={layer}
                    defects={defects}
                    isOpen={advancedSettingsLayerId === layer.id}
                    onToggle={() => onToggleAdvancedSettings(layer.id)}
                    onSave={(settings) => onVisualizationSettingsSave(layer.id, settings)}
                  />
                ) : null}
              </>
            ) : null}
          </li>
        );
      })}
    </ul>
  ) : (
    <div className="sdm-active-layers__empty" role="note">
      <p className="sdm-active-layers__empty-title">
        {embedded ? 'No layers yet' : 'No layers active'}
      </p>
      {/* <p className="sdm-active-layers__empty-body">
        {embedded
          ? 'Personal layers from the session appear here when active.'
          : 'Add a layer to start visualising survey data on the map.'}
      </p> */}
    </div>
  );

  if (embedded) {
    return (
      <div className="sdm-active-layers sdm-active-layers--embedded" aria-label="My Layers">
        {listOrEmpty}
      </div>
    );
  }

  return (
    <section className="sdm-active-layers" aria-label="Active layers">
      <header className="sdm-active-layers__header">
        <div>
          <h2 className="sdm-active-layers__title">Active layers</h2>
          <p className="sdm-active-layers__subtitle">
            {hasLayers
              ? `${layers.length} active layer${layers.length === 1 ? '' : 's'} (visible layers shown on map)`
              : 'No layers added yet'}
          </p>
        </div>
        <button
          type="button"
          className="sdm-active-layers__add"
          onClick={onAddLayer}
          aria-expanded={isAddLayerOpen}
          aria-busy={isAddLayerLoading || isAddingLayer || undefined}
          disabled={isAddingLayer}
        >
          {isAddingLayer
            ? 'Adding layer…'
            : isAddLayerOpen
              ? 'Cancel'
              : 'Add layer'}
        </button>
      </header>

      {listOrEmpty}
    </section>
  );
}

export default ActiveLayersPanel;
